/**
 * You, in the desktop header — the same panel the phone opens, hung under your own face.
 *
 * The header used to lay the identity block out flat: an `acting as` select, a role chip and a
 * Sign out link, three controls in a row competing with the tabs beside them. The phone has had
 * the better answer since the tab bar landed — your face opens one panel that holds everything
 * about you — and there was no reason for the two to disagree, which is what Tim asked for on
 * 21 August. So the desktop now opens the *same component*: `IdentityPanel`, unchanged, in a
 * popover instead of a sheet. One place to change, one thing to learn.
 *
 * It closes three ways, and they are independent for the same reason the sheet's are:
 *
 *   ESCAPE, wherever the focus is.
 *   A POINTER OUTSIDE THE MENU. On `pointerdown`, so a click that starts outside never also
 *   lands on the thing it was aimed at through a menu that is still open.
 *   CHOOSING SOMETHING. Sign out closes it, via the `close` handed to the children.
 *
 * A click on the trigger itself is deliberately not "outside": the button that opened the menu
 * closes it, which is the one gesture people try first.
 */

import { useEffect, useRef, type ReactNode } from 'react';
import { GmBadge } from './GmBadge.js';
import { managerShort } from '../domain/identity.js';
import type { GmId } from '../store/types.js';

export interface ProfileMenuProps {
  /** The manager being shown as — the face on the button, as on the phone's bar. */
  gm: GmId;
  /** Hover text for the trigger: the role, and whose team you are standing in. */
  title: string;
  open: boolean;
  onOpen: (open: boolean) => void;
  /** Non-manager roles carry the star the header chip used to. */
  starred: boolean;
  borrowed: boolean;
  /** How many sign-ins are waiting on the admin. Beats `borrowed` for the one dot. */
  waiting: number;
  children: (close: () => void) => ReactNode;
}

export function ProfileMenu(props: ProfileMenuProps) {
  const { gm, title, open, onOpen, starred, borrowed, waiting, children } = props;
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onOpen(false); };
    const onDown = (e: PointerEvent) => {
      /*
       * A SHEET THE MENU OPENED IS NOT "OUTSIDE" IT. Change photo is portalled to `document.body`
       * (see `dock`), so its stage is not inside `.mewrap` — and the first drag on it closed the
       * menu, which unmounted the panel, which unmounted the dialog mid-crop. The sheet and its
       * scrim belong to the menu for as long as they exist.
       */
      const t = e.target as Element | null;
      if (t?.closest?.('.kdsheet, .sheetscrim')) return;
      if (!wrap.current?.contains(e.target as Node)) onOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown);
    };
  }, [open, onOpen]);

  return (
    <div className="mewrap" ref={wrap}>
      <button type="button" className={`mebtn ${open ? 'on' : ''}`}
        aria-expanded={open} aria-haspopup="dialog" title={title}
        onClick={() => onOpen(!open)}>
        <GmBadge gm={gm} size="sm" className="meface" />
        {starred && <span className="ico" aria-hidden="true">★</span>}
        <span className="mename">{managerShort(gm)}</span>
        <span className="mecaret" aria-hidden="true" />
        {/*
          WAITING BEATS BORROWED, exactly as on the phone's bar button — both are one dot in the
          same corner, they cannot both be drawn, and the one that needs an action from you wins.
          The state that lost is still inside the menu.
        */}
        {waiting > 0 ? (
          <span className="navdot waiting" role="img"
            aria-label={`${waiting} waiting to be let in`}
            title={`${waiting} waiting to be let in — Admin › Access`} />
        ) : borrowed && <span className="navdot borrowed" aria-hidden="true" />}
      </button>
      {open && (
        <div className="memenu" role="dialog" aria-label="You">
          {children(() => onOpen(false))}
        </div>
      )}
    </div>
  );
}
